import { io, type Socket } from 'socket.io-client';
import { storage, STORAGE_KEYS } from '@utils/storage';
import type { AuthTokens } from '@/types';

export const CHAT_EVENTS = {
  MESSAGE_NEW: 'message:new',
  MESSAGE_READ: 'message:read',
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',
  UNREAD_UPDATE: 'unread:update',
  PRESENCE_UPDATE: 'presence:update',
  THREAD_JOIN: 'thread:join',
  THREAD_LEAVE: 'thread:leave',
} as const;

type Listener = (...args: unknown[]) => void;

class ChatSocketManager {
  private socket: Socket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private connectionRefCount = 0;

  acquire(): Socket | null {
    this.connectionRefCount += 1;
    return this.connectInternal();
  }

  release(): void {
    this.connectionRefCount = Math.max(0, this.connectionRefCount - 1);
    if (this.connectionRefCount === 0) {
      this.disconnectInternal();
    }
  }

  /** @deprecated Prefer acquire/release for lifecycle management. */
  connect(): Socket | null {
    return this.acquire();
  }

  /** @deprecated Prefer acquire/release for lifecycle management. */
  disconnect(): void {
    this.connectionRefCount = 0;
    this.disconnectInternal();
  }

  private connectInternal(): Socket | null {
    const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
    if (!tokens?.access_token) return null;

    if (this.socket?.connected) return this.socket;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const baseUrl = import.meta.env.VITE_WS_URL || window.location.origin;

    this.socket = io(`${baseUrl}/chat`, {
      path: '/socket.io',
      auth: { token: tokens.access_token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
    });

    this.socket.on('connect', () => {
      this.emitLocal('connect');
    });

    this.socket.on('disconnect', () => {
      this.emitLocal('disconnect');
    });

    for (const event of Object.values(CHAT_EVENTS)) {
      this.socket.on(event, (payload: unknown) => {
        this.emitLocal(event, payload);
      });
    }

    return this.socket;
  }

  private disconnectInternal(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  joinThread(partnerId: string): void {
    this.socket?.emit(CHAT_EVENTS.THREAD_JOIN, { partnerId });
  }

  leaveThread(): void {
    this.socket?.emit(CHAT_EVENTS.THREAD_LEAVE, {});
  }

  sendTyping(partnerId: string | undefined, orderId: string | undefined, isTyping: boolean): void {
    const event = isTyping ? CHAT_EVENTS.TYPING_START : CHAT_EVENTS.TYPING_STOP;
    this.socket?.emit(event, { partnerId, orderId });
  }

  on(event: string, listener: Listener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);
    return () => this.listeners.get(event)?.delete(listener);
  }

  private emitLocal(event: string, payload?: unknown): void {
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }

  get connected(): boolean {
    return Boolean(this.socket?.connected);
  }
}

export const chatSocket = new ChatSocketManager();
